import { useCallback, useMemo, useState } from 'react';

import { Status } from '../../../api/enumerations';

const STORAGE_KEY = 'home:pipeline:collapsed';

const readStored = (): Set<Status> => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(
      parsed.filter((value): value is Status => typeof value === 'number')
    );
  } catch {
    return new Set();
  }
};

const persist = (collapsed: Set<Status>): void => {
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(Array.from(collapsed))
    );
  } catch {
    // Storage may be unavailable (private mode); the in-memory state still works.
  }
};

export interface UsePipelineColumnsReturn {
  isCollapsed: (status: Status) => boolean;
  toggle: (status: Status) => void;
  expandAll: () => void;
  collapsedCount: number;
}

/**
 * Which pipeline columns the user has collapsed. Every column starts open;
 * the choice is remembered per browser so the board reopens the way it was
 * left.
 */
export function usePipelineColumns(): UsePipelineColumnsReturn {
  const [collapsed, setCollapsed] = useState<Set<Status>>(readStored);

  const isCollapsed = useCallback(
    (status: Status): boolean => collapsed.has(status),
    [collapsed]
  );

  const toggle = useCallback((status: Status): void => {
    setCollapsed((previous) => {
      const next = new Set(previous);
      if (next.has(status)) {
        next.delete(status);
      } else {
        next.add(status);
      }
      persist(next);
      return next;
    });
  }, []);

  const expandAll = useCallback((): void => {
    const next = new Set<Status>();
    persist(next);
    setCollapsed(next);
  }, []);

  return useMemo(
    () => ({ isCollapsed, toggle, expandAll, collapsedCount: collapsed.size }),
    [isCollapsed, toggle, expandAll, collapsed.size]
  );
}
